import { Link } from "react-router-dom";
import { CalendarClock, Coffee, Clock } from "lucide-react";
import { useDataSource } from "@/lib/dataSource";
import { SPORT_META } from "@/lib/odds";

const fmtDate = (iso) => new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: iso.length === 10 ? "numeric" : undefined });
const fmtWhen = (iso) => new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export function OffSeasonNotice({ sport, onBrowse, fallback = null, context = "picks" }) {
  const ds = useDataSource();
  const s = ds.seasons?.[sport];
  if (!s || s.status === "live") return fallback;
  const color = SPORT_META[sport]?.color;
  const off = s.status === "offseason";
  const Icon = off ? CalendarClock : s.status === "upcoming" ? Clock : Coffee;
  const what = context === "props" ? "Player props" : context === "standings" ? "Standings and form" : "Picks, props and same game parlays";
  return (
    <div className="fade-up mt-6 flex flex-col items-start gap-4 rounded-2xl border border-[#26334D] bg-[#121824] p-6 sm:flex-row sm:items-center" data-testid="offseason-notice" data-status={s.status}>
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl" style={{ background: `${color}22`, color }}><Icon size={22} /></span>
      <div className="min-w-0 flex-1">
        <p className="label-caps" style={{ color }} data-testid="offseason-league">{s.league}</p>
        {off && (
          <>
            <p className="mt-1 text-base font-semibold text-white" data-testid="offseason-title">{s.label} is in its off-season</p>
            <p className="mt-1 text-sm text-slate-400" data-testid="offseason-body">
              {s.season_start ? <>Season starts <span className="font-semibold text-emerald-300">{fmtDate(s.season_start)}</span> (expected). </> : "No season dates posted yet. "}
              {what} return the day sportsbooks post the first lines.
            </p>
          </>
        )}
        {s.status === "upcoming" && s.next_game && (
          <>
            <p className="mt-1 text-base font-semibold text-white" data-testid="offseason-title">No {s.label} games today</p>
            <p className="mt-1 text-sm text-slate-400" data-testid="offseason-body">
              Next up: <Link to={`/games/${s.next_game.event_id}`} data-testid="offseason-next-game" className="font-semibold text-emerald-300 hover:underline">{s.next_game.matchup}</Link> · {fmtWhen(s.next_game.start_time)}. {what} land once the slate opens.
            </p>
          </>
        )}
        {s.status === "break" && (
          <>
            <p className="mt-1 text-base font-semibold text-white" data-testid="offseason-title">Nothing on the {s.label} board right now</p>
            <p className="mt-1 text-sm text-slate-400" data-testid="offseason-body">{s.label} runs year-round — the next card shows here as soon as books post lines.</p>
          </>
        )}
      </div>
      {onBrowse && <button data-testid="offseason-browse-all" onClick={onBrowse} className="btn-primary shrink-0 !px-4 !py-2 text-xs">Browse all sports</button>}
    </div>
  );
}
